import React, { useState } from 'react';
import { Upload, Image, Button, Popconfirm, message, Spin } from 'antd';
import { CameraOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import axiosInstance from '../utils/axios';
import { hssColors } from '../config/theme';

const BASE = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '')
  : 'http://localhost:5001';

const FotoUpload = ({ urlEndpoint, uploadExtra, deleteEndpoint, fotoActual, label, onSuccess, onDelete, disabled }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);

  const src = fotoActual
    ? (fotoActual.startsWith('http') ? fotoActual : `${BASE}${fotoActual}`)
    : null;

  const beforeUpload = async (file) => {
    const valido = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type);
    if (!valido) { message.error('Solo se permiten imágenes JPG, PNG o WEBP'); return false; }
    if (file.size > 10 * 1024 * 1024) { message.error('El archivo no debe superar 10 MB'); return false; }

    const formData = new FormData();
    formData.append('file', file);
    if (uploadExtra) {
      Object.entries(uploadExtra).forEach(([k, v]) => formData.append(k, v));
    }

    setLoading(true);
    try {
      const res = await axiosInstance.post(urlEndpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success('Foto guardada');
      onSuccess(res.data.url);
    } catch {
      message.error('Error al subir la foto');
    } finally {
      setLoading(false);
    }
    return false; // previene upload automático de antd
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axiosInstance.delete(deleteEndpoint || urlEndpoint);
      message.success('Foto eliminada');
      onDelete();
    } catch {
      message.error('Error al eliminar la foto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ fontSize: 12, color: hssColors.textSecondary, marginBottom: 6, fontWeight: 600 }}>
        {label}
      </div>

      {src ? (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img
            src={src}
            alt={label}
            style={{
              width: 120, height: 90, objectFit: 'cover',
              borderRadius: 8, border: `1px solid ${hssColors.gray200}`,
              cursor: 'pointer', display: 'block',
            }}
            onClick={() => setPreview(true)}
          />
          {/* Acciones hover */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: 8,
            background: 'rgba(0,0,0,0.45)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', gap: 8,
            opacity: 0, transition: 'opacity 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.opacity = 1}
            onMouseLeave={e => e.currentTarget.style.opacity = 0}
          >
            <Button
              size="small" type="text" icon={<EyeOutlined />}
              style={{ color: '#fff' }} onClick={() => setPreview(true)}
            />
            {!disabled && (
              <>
                <Upload beforeUpload={beforeUpload} showUploadList={false} accept="image/*">
                  <Button size="small" type="text" icon={<CameraOutlined />} style={{ color: '#fff' }} />
                </Upload>
                <Popconfirm title="¿Eliminar foto?" onConfirm={handleDelete} okText="Sí" cancelText="No">
                  <Button size="small" type="text" icon={<DeleteOutlined />} style={{ color: '#ff7875' }} />
                </Popconfirm>
              </>
            )}
          </div>
          {loading && (
            <div style={{
              position: 'absolute', inset: 0, borderRadius: 8,
              background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Spin size="small" />
            </div>
          )}
          {/* Preview fullscreen */}
          <Image
            style={{ display: 'none' }}
            src={src}
            preview={{ visible: preview, onVisibleChange: setPreview }}
          />
        </div>
      ) : (
        <Upload beforeUpload={beforeUpload} showUploadList={false} accept="image/*" disabled={disabled}>
          <div style={{
            width: 120, height: 90, borderRadius: 8,
            border: `2px dashed ${hssColors.gray200}`,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', cursor: disabled ? 'not-allowed' : 'pointer',
            background: hssColors.gray100, gap: 4,
            opacity: disabled ? 0.5 : 1,
          }}>
            {loading
              ? <Spin size="small" />
              : <>
                  <CameraOutlined style={{ fontSize: 22, color: hssColors.gray400 }} />
                  <span style={{ fontSize: 11, color: hssColors.gray400 }}>Subir foto</span>
                </>
            }
          </div>
        </Upload>
      )}
    </div>
  );
};

export default FotoUpload;
